import {useQuery} from "../../../utils/useQuery";
import useGetCheckData from "./useGetCheckData";
import {
  changeCheckProjectRequest,
  createCheckRequest,
  editCheckRequest,
  groupCancelSource
} from "../../../services/api";
import {showNotification} from "../../../utils/actionWrappers";
import i18next from "../../../services/language/i18next";
import {useParams} from "react-router-dom";
import {useEffect, useState} from "react";
import {CHECK_TYPES, ERR_MESSAGES} from "../../../utils/constants";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import checkFormSchema from "../../../components/ReactHookForm/schemes/checkFormSchema";
import {
  getChecksDownTimeouts,
  getChecksTimeouts,
  getDefaultValues,
  getIntegrationsByType,
  getTimeouts,
  getTimeoutsType
} from "../CheckForm/CheckForm.model";

const useCheckFormConfig = () => {
  const query = useQuery();
  const clonedId = query.get("clonedId");
  const {id} = useParams();
  const {getCheckData, integrations, checkRules, isProjectAdmin} = useGetCheckData(clonedId || id, !!clonedId);
  const [options, setOptions] = useState({
    timeoutsOptions: [],
    downTimeoutsOptions: [],
    projectsOptions: [],
    integrationsOptions: []
  });

  const formControl = useForm({
    mode: "onChange",
    resolver: yupResolver(checkFormSchema),
    defaultValues: getDefaultValues()
  });
  const {watch, reset, getValues, setValue} = formControl;
  const typeValue = watch("type");

  const onTypeChange = async (typeValue) => {
    const formValues = getValues();

    const timeoutsType = getTimeoutsType(typeValue);
    const downTimeoutsOptions = getChecksDownTimeouts(timeoutsType);
    const timeoutsOptions = getChecksTimeouts(timeoutsType);

    const integrationsOptions = getIntegrationsByType(typeValue, integrations);

    setOptions((oldState) => ({...oldState, timeoutsOptions, downTimeoutsOptions, integrationsOptions}));
    await reset({
      ...formValues,
      type: typeValue,
      timeout: timeoutsOptions[0].value,
      down_timeout: downTimeoutsOptions[1].value
    }, {keepDirty: true});

    if (formValues.name) {
      setValue("name", formValues.name, {shouldDirty: false, shouldValidate: true});
    }
    setValue("description", formValues.description, {shouldDirty: false, shouldValidate: true});
    if (formValues.url) {
      setValue("url", formValues.url, {shouldDirty: true, shouldValidate: true});
    }
    if ([CHECK_TYPES.JIRA, CHECK_TYPES.BALANCE].includes(typeValue)) {
      const integrationsOptions = getIntegrationsByType(typeValue, integrations);
      const integrationValue = integrationsOptions[0].value;
      setValue("integration", integrationValue, {shouldDirty: !integrationValue, shouldValidate: !integrationValue});
    }
  };

  const onTimeoutChange = () => {
    const timeoutsType = getTimeoutsType(typeValue);
    const timeoutsOptions = getChecksTimeouts(timeoutsType);

    setOptions((oldState) => ({...oldState, timeoutsOptions}));
  };

  const onDownTimeoutChange = () => {
    const timeoutsType = getTimeoutsType(typeValue);
    const downTimeoutsOptions = getChecksDownTimeouts(timeoutsType);

    setOptions((oldState) => ({...oldState, downTimeoutsOptions}));
  };

  const addCheck = (checkData) => {
    return createCheckRequest(checkData)
      .then(() => showNotification(
        i18next.t("notification:title.success", "Success!"),
        i18next.t("notification:message.added.check", "Check has been successfully added."),
        "success"
      ));
  };

  const updateCheck = async (checkData, isProjectChanged, isCheckInfoChanged) => {
    isCheckInfoChanged && await editCheckRequest(checkData, id);
    isProjectChanged && await changeCheckProjectRequest(id, {project: checkData.project});

    showNotification(
      i18next.t("notification:title.success", "Success!"),
      i18next.t("notification:message.updated.check", "Check has been successfully updated."),
      "success"
    );
  };

  useEffect(() => {
    getCheckData()
      .then(({checkData, projects, integrations}) => {
        const typeValue = checkData?.type || getValues("type");
        const timeout = checkData?.timeout || getValues("timeout");
        const down_timeout = checkData?.down_timeout || getValues("down_timeout");

        const timeoutsType = getTimeoutsType(typeValue);
        const checksDownTimeoutsArr = getChecksDownTimeouts(timeoutsType);
        const checksTimeoutsArr = getChecksTimeouts(timeoutsType);
        const timeoutsOptions = getTimeouts(checksTimeoutsArr, timeout);
        const downTimeoutsOptions = getTimeouts(checksDownTimeoutsArr, down_timeout);

        const projectsOptions = projects.filter((project) => project.is_user_project_admin)
          .map((project) => ({value: project.id, name: project.name}));
        const integrationsOptions = getIntegrationsByType(typeValue, integrations);

        setOptions((oldState) => ({
          ...oldState,
          timeoutsOptions,
          downTimeoutsOptions,
          projectsOptions,
          integrationsOptions
        }));
        const checkFormValues = checkData || {
          ...getValues(),
          project: projects[0].id,
          down_timeout: downTimeoutsOptions[1].value,
          timeout: timeoutsOptions[0].value
        };
        reset(getDefaultValues(checkFormValues));
      });
    return () => {
      groupCancelSource.value?.cancel(ERR_MESSAGES.CANCELLED_REQUEST);
      groupCancelSource.value = null;
    };
  }, [getCheckData, reset, getValues]);

  const editCheckConfig = {
    onSaveButtonClick: updateCheck,
    onTypeChange,
    onTimeoutChange,
    onDownTimeoutChange,
    checkRules,
    isProjectAdmin,
    id,
    formId: "check-edit-form",
    formControl: {
      ...formControl,
      typeValue,
      options
    }
  };

  const addCheckConfig = {
    onSaveButtonClick: addCheck,
    onTypeChange,
    onTimeoutChange,
    onDownTimeoutChange,
    checkRules,
    isClonedCheck: !!clonedId,
    formId: "check-add-form",
    formControl: {
      ...formControl,
      typeValue,
      options
    }
  };
  return id ? editCheckConfig : addCheckConfig;
};
export default useCheckFormConfig;